import React, { useState, useEffect, useCallback } from "react";
import MenuForm from "../UploadMenu/MenuForm";
import api, { pusher } from "../../services/api";
import toast from "react-hot-toast";

const MenuManagement = () => {
  const [menuItems, setMenuItems] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [showForm, setShowForm] = useState(false);

  // Filters for Menu Management
  const [filterBrand, setFilterBrand] = useState("all");
  const [filterCategory, setFilterCategory] = useState("all");

  const fetchMenuItems = useCallback(async () => {
    try {
      const response = await api.get("/menu?isAvailable=all");
      if (response.data.success) {
        setMenuItems(response.data.data);
      }
    } catch (error) {
      console.error("Fetch Menu Error:", error);
      toast.error("Failed to fetch menu items");
    }
  }, []);

  useEffect(() => {
    if (!showForm) {
      fetchMenuItems();
    }
  }, [showForm, fetchMenuItems]);

  useEffect(() => {
    // Pusher for Real-time Menu Updates
    const channel = pusher.subscribe("menu-channel");

    channel.bind("menu_updated", (data) => {
      console.log("🔄 Menu update received in Admin:", data);
      fetchMenuItems();
    });

    return () => {
      channel.unbind_all();
      pusher.unsubscribe("menu-channel");
    };
  }, [fetchMenuItems]);

  const handleRefreshApp = async () => {
    try {
      const loadingToast = toast.loading("Updating menu for all users...");
      const response = await api.post("/menu/refresh");
      if (response.data.success) {
        toast.success("Menu updated for all users!", { id: loadingToast });
      } else {
        toast.error("Failed to update menu", { id: loadingToast });
      }
    } catch (error) {
      console.error("Refresh App Error:", error);
      toast.error("An error occurred while updating menu");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this item?")) {
      try {
        const response = await api.delete(`/menu/${id}`);
        if (response.data.success) {
          toast.success("Menu item deleted");
          fetchMenuItems();
        }
      } catch (error) {
        console.error("Delete Menu Error:", error);
        toast.error("Failed to delete item");
      }
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setShowForm(true);
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingItem(null);
    fetchMenuItems();
  };

  if (showForm) {
    return (
      <MenuForm
        initialData={editingItem}
        isEdit={!!editingItem}
        onCancel={() => {
          setShowForm(false);
          setEditingItem(null);
        }}
        onSuccess={handleFormSuccess}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-800">
          Menu Items
        </h2>
        <div className="flex flex-col md:flex-row gap-4">
          {/* Brand Filter */}
          <select
            value={filterBrand}
            onChange={(e) => {
              setFilterBrand(e.target.value);
              setFilterCategory("all");
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 bg-white"
          >
            <option value="all">All Brands</option>
            <option value="gravy_ganj">Gravy Ganj</option>
            <option value="kepsa_queue">Kepsa Queue</option>
            <option value="gutful_greens">Gutful Goodness - Greens</option>
          </select>

          {/* Category Filter */}
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 bg-white"
          >
            <option value="all">All Categories</option>
            {[...new Set(menuItems
              .filter(item => filterBrand === "all" || item.brandType === filterBrand)
              .map(item => item.category))]
              .sort()
              .map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))
            }
          </select>

          <button
            onClick={handleRefreshApp}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Update Data (All Users)
          </button>

          <button
            onClick={() => {
              setEditingItem(null);
              setShowForm(true);
            }}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
          >
            + Add New Item
          </button>
        </div>
      </div>

      {menuItems.length === 0 ? (
        <div className="text-center py-10 text-gray-500">
          No menu items found. Add one to get started!
        </div>
      ) : (
        <div className="space-y-8">
          {/* Group items by brand */}
          {['gravy_ganj', 'kepsa_queue', 'gutful_greens']
            .filter(brand => filterBrand === "all" || brand === filterBrand)
            .map((brand) => {
              const brandItems = menuItems.filter(item =>
                item.brandType === brand &&
                (filterCategory === "all" || item.category === filterCategory)
              );
              if (brandItems.length === 0) return null;

              const categories = [...new Set(brandItems.map(item => item.category))];

              return (
                <div key={brand} className="bg-gray-50 rounded-xl p-6 border-2 border-gray-200">
                  <div className="mb-6">
                    <h3 className="text-2xl font-bold text-gray-900 capitalize mb-1">
                      {brand.replace(/_/g, ' ')}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {brandItems.length} item{brandItems.length !== 1 ? 's' : ''} • {categories.length} categor{categories.length !== 1 ? 'ies' : 'y'}
                    </p>
                  </div>

                  <div className="space-y-6">
                    {categories.map((category) => {
                      const categoryItems = brandItems.filter(item => item.category === category);

                      return (
                        <div key={category}>
                          <div className="mb-4">
                            <h4 className="text-lg font-semibold text-gray-700 capitalize flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full bg-green-600"></span>
                              {category}
                              <span className="text-sm font-normal text-gray-500">
                                ({categoryItems.length})
                              </span>
                            </h4>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {categoryItems.map((item) => (
                              <div
                                key={item._id}
                                className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200 flex flex-col hover:shadow-md transition-shadow"
                              >
                                <div className="h-40 overflow-hidden relative">
                                  <img
                                    src={item.image || "https://via.placeholder.com/400"}
                                    alt={item.name}
                                    className="w-full h-full object-cover"
                                  />
                                  {!item.isAvailable && (
                                    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                                      <span className="text-white font-bold px-3 py-1 bg-red-500 rounded-full text-sm">
                                        Unavailable
                                      </span>
                                    </div>
                                  )}
                                  {item.specialTag && item.specialTag !== "none" && (
                                    <div className="absolute top-2 right-2">
                                      <span className={`text-white text-xs font-bold px-2 py-1 rounded-full ${item.specialTag === "chef_special" ? "bg-yellow-500" :
                                          item.specialTag === "most_ordered" ? "bg-orange-500" :
                                            item.specialTag === "trending" ? "bg-pink-500" :
                                              item.specialTag === "new_arrival" ? "bg-blue-500" :
                                                item.specialTag === "bestseller" ? "bg-purple-500" :
                                                  item.specialTag === "seasonal" ? "bg-green-500" :
                                                    "bg-gray-500"
                                        }`}>
                                        {item.specialTag === "chef_special" ? "Chef's Special" :
                                          item.specialTag === "most_ordered" ? "Most Ordered" :
                                            item.specialTag === "trending" ? "Trending" :
                                              item.specialTag === "new_arrival" ? "New Arrival" :
                                                item.specialTag === "bestseller" ? "Bestseller" :
                                                  item.specialTag === "seasonal" ? "Seasonal" :
                                                    item.specialTag}
                                      </span>
                                    </div>
                                  )}
                                </div>
                                <div className="p-4 flex-1 flex flex-col">
                                  <div className="flex justify-between items-start mb-2">
                                    <h3 className="font-bold text-gray-900 text-base line-clamp-1">
                                      {item.name}
                                    </h3>
                                    <span className="text-green-600 font-bold text-sm whitespace-nowrap ml-2">
                                      ₹{item.price || item.portions?.[0]?.price}
                                    </span>
                                  </div>
                                  <p className="text-gray-500 text-xs mb-3 line-clamp-2 flex-1">
                                    {item.description}
                                  </p>

                                  <div className="mb-3">
                                    <span className={`inline-block px-2 py-1 text-xs font-semibold rounded ${item.dietaryType === 'veg'
                                        ? 'bg-green-100 text-green-800'
                                        : item.dietaryType === 'non-veg'
                                          ? 'bg-red-100 text-red-800'
                                          : 'bg-yellow-100 text-yellow-800'
                                      }`}>
                                      {item.dietaryType === 'veg' ? '🌱 Veg' : item.dietaryType === 'non-veg' ? '🍖 Non-Veg' : '🥚 Contains Egg'}
                                    </span>
                                  </div>

                                  <div className="flex gap-2 mt-auto">
                                    <button
                                      onClick={() => handleEdit(item)}
                                      className="flex-1 px-3 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition font-medium text-sm"
                                    >
                                      Edit
                                    </button>
                                    <button
                                      onClick={() => handleDelete(item._id)}
                                      className="flex-1 px-3 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition font-medium text-sm"
                                    >
                                      Delete
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
};

export default MenuManagement;
